import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import type { ReactNode } from 'react';
import type { AnomalyMetricsDto } from '@oip/contracts';
import { CardIcon } from '../../components/common/CardIcon';
import type { CardHue } from '../../components/common/hues';

/**
 * Whether this detector is worth trusting yet — the six gates of design §19.0b.
 *
 * Built from a customer's mockup, and reworded on the same report that reached
 * the outcome vocabulary: *"difficult to understand what it means for a non-tech
 * user"*. **Precision**, **review rate**, **against the budget** and
 * **suppression** are all our words for our own mechanisms; the reader is a
 * transport manager asking *is this thing any good, and is anybody reading it*.
 *
 * ⚠️ **FIVE departures from the mockup.** Each is recorded because each is the
 * sort of thing somebody restores in good faith.
 *
 * 1. **NOTHING IS "READY FOR PRODUCTION", and nothing is marked ready
 *    automatically.** The mockup's footer promises *"if the metrics meet the
 *    thresholds, the detector will be marked as ready for production"* — an
 *    automation that does not exist. Publication is a setting a PLATFORM
 *    administrator changes by hand, deliberately: shadow mode exists so a
 *    detector nobody has calibrated cannot start creating work, and a screen
 *    that promised to flip itself would describe the opposite of that control.
 *    The footer says what actually happens next instead.
 * 2. **`precision` and `falsePositiveRate` SUM TO 1 by construction** —
 *    `confirmed / judged` and `falsePositive / judged` over the same denominator
 *    — so two tiles reading 80% and 20% are ONE fact stated twice, and the
 *    mockup files them in different sections, where a reader could not see they
 *    are complements. One tile, with the false-alarm share stated inside it.
 * 3. **The COLOURS, again.** The mockup circles *Review engagement* in red at
 *    0% and paints the budget bar green. A branch that publishes nothing has a
 *    review rate of 0% correctly — red would claim a fault on the day the
 *    feature is switched on — and being AT the budget is the budget WORKING,
 *    since the allowance exists to hold items back. `hues.ts` for the tiles. The
 *    publishing pill keeps a semantic colour because it states a MODE rather
 *    than a measurement, which is what the card it replaced already did.
 * 4. **A withheld figure says PENDING and why, never 0%.** The mockup does this
 *    for accuracy and it is the rule everywhere here: "we cannot say yet" and
 *    "the answer is zero" point in opposite directions.
 * 5. **"View findings" SCROLLS; it does not navigate.** The findings are on this
 *    page. A link that reloaded the same screen would lose the window.
 */
export function ReadinessCard({
  metrics,
  onViewFindings,
}: {
  metrics: AnomalyMetricsDto;
  onViewFindings: () => void;
}): JSX.Element {
  const { publishing, outcomes, needed } = metrics;
  const enoughEvidence = outcomes >= needed;

  /*
   * ⚠️ THREE states and not one of them is "ready". The platform can say what it
   * has measured; whether that is good enough to switch on is a judgement, and
   * a screen that announced readiness would be making it on somebody's behalf.
   */
  const status = !publishing
    ? {
        icon: <PauseCircleOutlineIcon />,
        hue: 'slate' as CardHue,
        headline:
          metrics.published > 0 ? 'No longer reaching anybody' : 'Not reaching anybody yet',
        /*
         * ⚠️ **`publishing` is the mode NOW; `published` counts the WINDOW**, so
         * a branch switched back to shadow legitimately shows findings that did
         * reach people — and the first version said "nothing found here reaches
         * anybody" directly above a tile reading "0 of 4 opened". One card
         * contradicting itself, which is the Enabled/Availability fault in a
         * third costume. **Found by screenshotting; every figure was correct.**
         * The wording matches the publication control's own: returning to
         * shadow stops creating work, it does not withdraw it.
         */
        detail:
          metrics.published > 0
            ? `Every unusual day is still being found, judged and stored, and none of it reaches anybody now. The ${metrics.published} already published stay in the queue — going back to shadow stops creating work, it does not withdraw it.`
            : 'Every unusual day is still being found, judged and stored — none of it becomes an incident or reaches a queue. Nothing can be reviewed until it does, which is why the figures below read zero.',
      }
    : enoughEvidence
      ? {
          icon: <CheckCircleOutlineIcon />,
          hue: 'cyan' as CardHue,
          headline: 'Enough has been reviewed to judge it',
          detail:
            'Enough findings have been judged either way for the accuracy figure below to mean something. It keeps moving as more are reviewed.',
        }
      : {
          icon: <HourglassEmptyIcon />,
          hue: 'indigo' as CardHue,
          headline: 'Still gathering evidence',
          detail:
            'Findings are reaching people, but not enough have been judged yet to say how often this detector is right. Reviewing findings is what builds that up.',
        };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flexWrap: 'wrap', rowGap: 1 }}
      >
        {/*
          ⚠️ The one SEMANTIC colour on this card, because it states a MODE
          rather than a measurement — the card it replaced already used
          success/info for exactly this, and keeping it means the two halves of
          the screen do not disagree about what publishing looks like.
        */}
        <Chip
          size="small"
          color={publishing ? 'success' : 'default'}
          label={publishing ? 'Publishing' : 'In shadow'}
        />
        <Typography variant="body2" color="text.secondary">
          {publishing
            ? 'Unusual days become incidents and reach this branch’s queue.'
            : 'New findings reach nobody. This is how a detector is measured before it starts making work.'}
        </Typography>
      </Stack>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack direction="row" spacing={1.5} sx={{ flexGrow: 1 }}>
            <CardIcon hue={status.hue}>{status.icon}</CardIcon>
            <Box>
              <Typography variant="h6">{status.headline}</Typography>
              <Typography variant="body2" color="text.secondary">
                {status.detail}
              </Typography>
            </Box>
          </Stack>

          {/*
            ⚠️ The progress toward being able to SAY anything, kept beside the
            status because it is the reason for it. Clamped at 100: more reviews
            than the floor needs is not 140% of an answer.
          */}
          <Box sx={{ minWidth: { xs: '100%', md: 260 } }}>
            <Typography variant="overline" color="text.secondary" display="block">
              Findings judged so far
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {outcomes} / {needed}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, needed > 0 ? (outcomes / needed) * 100 : 0)}
              sx={{ mt: 0.5, height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Needed before we will put a number on how often it is right
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        {/*
          ⚠️ ONE tile for accuracy, with the false-alarm share INSIDE it.
          `precision` and `falsePositiveRate` are computed over the same
          denominator and sum to 1, so a second tile would state one fact twice
          — and in the mockup's layout, in a different section, where nobody
          could see they were complements.
        */}
        <Signal
          hue="indigo"
          icon={<TrackChangesIcon />}
          label="How often it is right"
          value={metrics.precision === null ? null : percent(metrics.precision)}
          pending={`${outcomes} of ${needed} findings judged`}
          note={
            metrics.precision === null
              ? 'Once enough findings have been judged, this says what share of them turned out to be real.'
              : `Of the findings somebody judged, this share was a real problem — the other ${percent(
                  metrics.falsePositiveRate ?? 1 - metrics.precision,
                )} were false alarms.`
          }
        />
        {/*
          ⚠️ §18: watch this MORE closely than accuracy. A detector can be
          perfectly right and still ignored, and by the time accuracy moves the
          readers have already stopped looking.
        */}
        <Signal
          hue="cyan"
          icon={<VisibilityIcon />}
          label="Findings people opened"
          value={metrics.reviewRate === null ? null : percent(metrics.reviewRate)}
          pending={metrics.published === 0 ? 'Nothing published to open yet' : null}
          note={
            metrics.published === 0
              ? 'Nothing has reached anybody, so there is nothing to open.'
              : `${metrics.opened} of ${metrics.published} opened. If this falls, people have stopped reading them — which shows up here long before accuracy moves.`
          }
        />
        <Signal
          hue="steel"
          icon={<BarChartIcon />}
          label="How much of the alert allowance is used"
          value={
            metrics.publishedAgainstBudget === null
              ? null
              : percent(metrics.publishedAgainstBudget)
          }
          pending={metrics.budgetAllowance === 0 ? 'No allowance set' : null}
          /*
           * ⚠️ NEUTRAL, not green-for-good. Reaching the allowance is the
           * allowance WORKING — it exists to hold findings back so a branch is
           * never flooded — so a colour saying "within budget" would imply a
           * problem the moment it is fully used.
           */
          bar={metrics.publishedAgainstBudget}
          note={`${metrics.published} of ${metrics.budgetAllowance} allowed. Anything beyond the allowance is held back rather than thrown away.`}
        />
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary" display="block">
          Other things worth watching
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mt: 0.5,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          }}
        >
          <Signal
            hue="violet"
            icon={<FilterAltIcon />}
            label="Days it could not judge"
            value={metrics.suppressionRate === null ? null : percent(metrics.suppressionRate)}
            pending={null}
            /*
             * ⚠️ A sudden RISE is a data problem wearing the costume of a quiet
             * week: everything refused looks exactly like a fleet with nothing
             * wrong, and only one of those needs somebody.
             */
            note="If this climbs, it usually means readings stopped arriving — not that the fleet had a quiet week."
          />
          <Signal
            hue="plum"
            icon={<DescriptionIcon />}
            label="Real problems found"
            value={
              metrics.confirmedPer100VehiclesPerMonth === null
                ? null
                : /*
                   * ⚠️ ROUNDED for display, never in the domain: the shared
                   * `round` gives three decimals, which is right for a 0–1 rate
                   * and reads as false precision on a count.
                   */
                  String(Math.round(metrics.confirmedPer100VehiclesPerMonth))
            }
            pending={null}
            note="Per 100 vehicles per month, counting only findings somebody confirmed were real."
          />
        </Box>
      </Box>

      {/*
        ⚠️ **What happens next says what a PERSON does.** The mockup promised
        the detector "will be marked as ready for production" once thresholds
        are met; nothing marks anything. Publication is a setting a platform
        administrator changes by hand, which is the whole point of shadow mode —
        a screen that implied it flips itself would describe the opposite of the
        control that exists.
      */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">What happens next</Typography>
            <Typography variant="body2" color="text.secondary">
              {!publishing
                ? 'Everything here keeps being measured. Switching publishing on is a decision your platform administrator makes, once these figures look right.'
                : enoughEvidence
                  ? 'Keep reviewing findings. The figures above move as more are judged, and whether they are good enough is a decision somebody makes — nothing switches itself.'
                  : `Review findings as they arrive. Once ${needed} have been judged either way, the accuracy figure above starts being shown.`}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onViewFindings}
            sx={{
              flexShrink: 0,
              cursor: 'pointer',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'transparent',
              color: 'primary.main',
              px: 2,
              py: 0.75,
              font: 'inherit',
              fontWeight: 600,
            }}
          >
            View findings
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}

/**
 * One measurement, or an honest statement that there is not one yet.
 *
 * ⚠️ **A withheld figure SAYS SO rather than rendering 0% or a blank.** These
 * are the numbers a decision to switch on is argued from, and "we cannot say
 * yet" and "the answer is zero" point in opposite directions — the rule
 * `judgePolicyVersion` and `kmPerL` already follow.
 */
function Signal({
  hue,
  icon,
  label,
  value,
  pending,
  note,
  bar,
}: {
  hue: CardHue;
  icon: ReactNode;
  label: string;
  value: string | null;
  /** Shown under "Pending" — why there is no figure. */
  pending: string | null;
  note: string;
  /** 0–1, drawn as a neutral bar. Omitted for signals with no ceiling. */
  bar?: number | null;
}): JSX.Element {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
    >
      <CardIcon hue={hue} size="small">
        {icon}
      </CardIcon>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary" display="block">
          {label}
        </Typography>
        {value === null ? (
          <>
            <Typography variant="h5" color="text.secondary">
              Pending
            </Typography>
            {pending && (
              <Typography variant="body2" color="text.secondary">
                {pending}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </Typography>
        )}
        {typeof bar === 'number' && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, bar * 100)}
            sx={{ mt: 0.75, height: 6, borderRadius: 1 }}
          />
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {note}
        </Typography>
      </Box>
    </Stack>
  );
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}
